import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface Category {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

const CategoryManager = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken bir hata oluştu');
    }
  };

  const handleAdd = () => {
    setSelectedCategory(null);
    setFormData({
      name: '',
      description: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
    });
    setIsModalOpen(true);
  };

  const supabaseErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') return '';
    const e = error as { message?: string; details?: string; hint?: string; code?: string };
    return [e.message, e.details, e.hint].filter(Boolean).join(' — ');
  };

  const handleDelete = async (id: string) => {
    const category = categories.find((c) => c.id === id);
    if (!category) return;

    if (!window.confirm(`"${category.name}" kategorisi silinsin mi?`)) return;

    try {
      const { data: linkedForums, error: forumError } = await supabase
        .from('forums')
        .select('id, name')
        .eq('category', category.name)
        .limit(5);

      if (forumError) throw forumError;

      if (linkedForums && linkedForums.length > 0) {
        toast.error(
          `Bu kategoriye bağlı ${linkedForums.length}+ forum var (ör. ${linkedForums[0].name}). Önce forumları başka kategoriye taşıyın.`
        );
        return;
      }

      const { data: suggestionRows } = await supabase
        .from('forum_suggestions')
        .select('id')
        .eq('category_id', id);

      if (suggestionRows?.length) {
        const suggestionIds = suggestionRows.map((r) => r.id);
        await supabase.from('suggestion_votes').delete().in('suggestion_id', suggestionIds);
        const { error: clearSuggestionsError } = await supabase
          .from('forum_suggestions')
          .delete()
          .eq('category_id', id);

        if (clearSuggestionsError) throw clearSuggestionsError;
      }

      await supabase.from('category_stats_history').delete().eq('category_id', id);
      await supabase.from('category_stat_snapshots').delete().eq('category_name', category.name);

      const { data: deleted, error: deleteError } = await supabase
        .from('categories')
        .delete()
        .eq('id', id)
        .select('id');

      if (deleteError) throw deleteError;

      if (!deleted?.length) {
        toast.error(
          'Kategori silinemedi. forumadmin ile giriş yaptığınızdan emin olun; sayfayı yenileyip tekrar deneyin (Ctrl+F5).'
        );
        return;
      }

      setCategories(categories.filter((c) => c.id !== id));
      toast.success('Kategori başarıyla silindi');
    } catch (error) {
      console.error('Error deleting category:', error);
      const detail = supabaseErrorMessage(error);
      if (detail.includes('foreign key') || detail.includes('violates') || detail.includes('23503')) {
        toast.error(
          'Kategori başka kayıtlara bağlı kaldı. Önce forum önerileri listesinden bu kategorideki kayıtları silin veya reddedin, sonra tekrar deneyin.'
        );
      } else {
        toast.error(detail ? `Kategori silinemedi: ${detail}` : 'Kategori silinirken bir hata oluştu');
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        toast.error('Kategori adı boş olamaz');
        setLoading(false);
        return;
      }

      // Build query incrementally to avoid syntax error
      let query = supabase
        .from('categories')
        .select('id')
        .eq('name', formData.name);

      // Only add the neq filter if we're editing an existing category
      if (selectedCategory?.id) {
        query = query.neq('id', selectedCategory.id);
      }

      const { data: existingCategory, error: checkError } = await query.maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;
      
      if (existingCategory) {
        toast.error('Bu kategori adı zaten kullanılıyor');
        setLoading(false);
        return;
      }

      if (selectedCategory) {
        const { error: updateError } = await supabase
          .from('categories')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim(),
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedCategory.id);

        if (updateError) throw updateError;

        // Update forums with new category name
        const { error: forumUpdateError } = await supabase
          .from('forums')
          .update({ category: formData.name.trim() })
          .eq('category', selectedCategory.name);

        if (forumUpdateError) throw forumUpdateError;

        const { error: suggestionUpdateError } = await supabase
          .from('forum_suggestions')
          .update({ category: formData.name.trim() })
          .eq('category_id', selectedCategory.id);

        if (suggestionUpdateError) throw suggestionUpdateError;

        toast.success('Kategori başarıyla güncellendi');
      } else {
        const { data, error: insertError } = await supabase
          .from('categories')
          .insert([{
            name: formData.name.trim(),
            description: formData.description.trim(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        if (!data) throw new Error('Kategori eklenemedi');

        toast.success('Kategori başarıyla eklendi');
      }

      setIsModalOpen(false);
      await fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Kategori kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Kategoriler</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus size={20} />
          Yeni Kategori Ekle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map(category => (
          <div
            key={category.id}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {category.name}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="p-1 text-blue-600 hover:text-blue-800"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="p-1 text-red-600 hover:text-red-800"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {category.description || 'Açıklama yok'}
            </p>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Oluşturulma: {new Date(category.created_at).toLocaleDateString('tr-TR')}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

          <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-md w-full mx-4 p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {selectedCategory ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}
            </Dialog.Title>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kategori Adı
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (selectedCategory ? 'Güncelle' : 'Ekle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default CategoryManager;